import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { GraphQLError } from "graphql";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { isAbortError, products, productsPretty } from "helpers";
import {
  Endpoint,
  GetUserEndpointsQueryVariables,
  PreLoginMutationVariables,
  Product,
  UserEndpointResult,
} from "operations/schema/schema";
import { useAppDispatch, useAppSelector } from "store";
import { getUserEndpoints, preLogin, setAuth0 } from "store/slices/user.store";

import PrimaryButton from "components/PrimaryButton";
import StyledTextField from "components/StyledTextField";
import { appEnvironment } from "env";

type LoginFormProps = {
  classes: any;
  email: string | undefined;
  preLoginLoading: boolean;
  setPreLoginLoading: (state: boolean) => void;
  setEmail: React.Dispatch<React.SetStateAction<string | undefined>>;
  setLoginUrl: (state: string) => void;
  setLoginProduct: (state: Product | undefined) => void;
  setIsLogin: (state: boolean) => void;
  url: string;
  urlSystem: string;
};

export const PreLoginForm: FC<LoginFormProps> = ({
  classes,
  email,
  preLoginLoading,
  setPreLoginLoading,
  setEmail,
  setLoginUrl,
  setLoginProduct,
  setIsLogin,
  url,
  urlSystem,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { authVar } = useAppSelector((s) => s.user);
  const navigate = useNavigate();
  const voApiUrl = "https://anywhere-api.vantage.online/";
  const asolviProtectApiUrl = "https://anywhere-api.asolviprotect.com/";
  const appEnv = appEnvironment();
  const [loginInitiated, setLoginInitiated] = useState(false);
  const [firstTimeLogin, setFirstTimeLogin] = useState(false);
  const [userEndpoints, setUserEndpoints] = useState<Endpoint[]>([]);
  const [engineerMultipleEndpoints, setEngineerMultipleEndpoints] = useState(false);
  const [useMultipleEndpoints, setUseMultipleEndpoints] = useState(false);
  const [endpoint, setEndpoint] = useState<Endpoint>();
  const [newEndpointUrl, setNewEndpointUrl] = useState("");
  const [newEndpointSystem, setNewEndpointSystem] = useState(products[0].value);
  const [addEndpointDialogOpen, setAddEndpointDialogOpen] = useState(false);
  const [userEndpointsResponse, setUserEndpointsResponse] = useState<UserEndpointResult>();
  const [userEndpointsError, setUserEndpointsError] = useState<string | GraphQLError[]>();

  const addNewEndpoint = () => {
    if (!newEndpointUrl) return;
    let newEndpoint: Endpoint = {
      apiUrl: parseEndpointUrl(newEndpointUrl),
      product: newEndpointSystem,
      apiVersion: "",
      id: "",
    };
    console.log(newEndpoint);
    if (!userEndpoints.map((e) => e.apiUrl).includes(newEndpoint.apiUrl)) {
      setUserEndpoints([...userEndpoints, newEndpoint]);
    }
    setEndpoint(newEndpoint);
    setAddEndpointDialogOpen(false);
  };
  const parseEndpointUrl = (url: string, system: string = newEndpointSystem): string => {
    if (!url) return url;
    url = url.includes("https://") ? url : `https://${url}`;
    if (system === Product.Evatic) {
      url = url.includes("/api") ? url : `${url}/api`;
    } else if (system === Product.VantageOnline || system === Product.AsolviProtect) {
      url = new URL("/", url).toString();
    }
    return url;
  };

  const preLoginCallback = useCallback(
    async (variables: PreLoginMutationVariables) => {
      await dispatch(preLogin(variables))
        .then(unwrapResult)
        .then((preLogin) => {
          if (preLogin && preLogin.domain && preLogin.clientId) {
            localStorage.setItem("auth0Props", JSON.stringify(preLogin));
            dispatch(setAuth0(preLogin));
            navigate("/login/auth0login");
          } else if (preLogin) {
            setLoginUrl(preLogin?.url || "");
            setLoginProduct(preLogin?.product || undefined);
            setIsLogin(true);
          }
          setPreLoginLoading(false);
        })
        .catch((e) => {
          if (isAbortError(e)) return;
          setPreLoginLoading(false);
        });
    },
    [dispatch, navigate, setIsLogin, setLoginProduct, setLoginUrl, setPreLoginLoading]
  );

  const getUserEndpointsCallback = useCallback(
    async (variables: GetUserEndpointsQueryVariables) => {
      await dispatch(getUserEndpoints(variables))
        .then(unwrapResult)
        .then(({ userEndpoints }) => {
          setUserEndpointsResponse(userEndpoints);
        })
        .catch((e) => {
          if (isAbortError(e)) return;
          setUserEndpointsError(e);
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (preLoginLoading) return;
    if (!!userEndpointsResponse && !userEndpointsError) {
      let { email, useMultipleEndpoints, lastEndpoint, userEndpoints } = userEndpointsResponse;
      setEngineerMultipleEndpoints(!!useMultipleEndpoints);
      // If useMultiple, set endpoints
      if (useMultipleEndpoints || !lastEndpoint) {
        setUserEndpoints(userEndpoints || []);
        setLoginInitiated(true);
        if (lastEndpoint) {
          setEndpoint(lastEndpoint);
          setNewEndpointSystem(lastEndpoint.product);
          if (lastEndpoint.product === Product.VantageOnline) {
            setNewEndpointUrl(voApiUrl);
          }
          if (lastEndpoint.product === Product.AsolviProtect) {
            setNewEndpointUrl(asolviProtectApiUrl);
          }
        } else {
          setFirstTimeLogin(true);
          setAddEndpointDialogOpen(true);
        }
      } else {
        // If not use multiple, login with last used
        let { apiUrl, product } = lastEndpoint;
        setPreLoginLoading(true);
        preLoginCallback({
          email: email || "",
          url: apiUrl,
          product: product,
        });
      }
    }
  }, [
    preLoginCallback,
    preLoginLoading,
    setPreLoginLoading,
    userEndpointsError,
    userEndpointsResponse,
  ]);

  // Allows login with old method
  useEffect(() => {
    if (urlSystem) {
      let newEndpoint: Endpoint = {
        apiUrl: "",
        product: Product.Mock,
        apiVersion: "",
        id: "",
      };
      if (urlSystem === Product.VantageOnline) {
        newEndpoint.apiUrl = voApiUrl;
        newEndpoint.product = Product.VantageOnline;
      } else if (urlSystem === Product.Evatic && url) {
        // Needed to extract these from `parseEndpoint()` to avoid inf-loop
        let apiUrl = url;
        apiUrl = apiUrl.includes("https://") ? apiUrl : `https://${apiUrl}`;
        apiUrl = apiUrl.includes("/api") ? apiUrl : `${apiUrl}/api`;
        //
        newEndpoint.apiUrl = apiUrl;
        newEndpoint.product = Product.Evatic;
      } else if (urlSystem === Product.AsolviProtect) {
        newEndpoint.apiUrl = asolviProtectApiUrl;
        newEndpoint.product = Product.AsolviProtect;
      }

      setNewEndpointUrl(newEndpoint.apiUrl);
      setNewEndpointSystem(newEndpoint.product);

      setLoginInitiated(true);
      setFirstTimeLogin(true);
      // setEndpointDialogDisabled(true);
      setUserEndpoints([newEndpoint]);
      setEndpoint(newEndpoint);
    }
  }, [url, urlSystem]);

  useEffect(() => {
    setUseMultipleEndpoints(
      engineerMultipleEndpoints || newEndpointSystem === Product.Evatic || appEnv !== "prod"
    );
  }, [appEnv, engineerMultipleEndpoints, newEndpointSystem]);

  const loginErrorMessage = (message: string) => {
    switch (message) {
      case "Auth0 failed":
        return intl.formatMessage({ id: "login.auth0.fail" });
      case "Your session has expired, please log in again":
        return intl.formatMessage({ id: "login.sessionExpired" });
      default:
        return message;
    }
  };

  return (
    <form
      className={classes.form}
      onSubmit={(e) => {
        e.preventDefault();
        if (loginInitiated) {
          if (!endpoint && newEndpointUrl) addNewEndpoint();
          let { apiUrl, product } =
            endpoint || {
              apiUrl: parseEndpointUrl(newEndpointUrl),
              product: newEndpointSystem,
            } ||
            {};
          let preLoginInput = {
            email: email || "",
            url: apiUrl,
            product: product,
          };
          setPreLoginLoading(true);
          preLoginCallback(preLoginInput);
        } else {
          if (!email) return;
          getUserEndpointsCallback({ email });
        }
      }}
      noValidate
    >
      <StyledTextField
        required
        id="email"
        label={intl.formatMessage({ id: "general.email" })}
        name="email"
        autoComplete="email"
        autoFocus
        value={email || ""} // ` || ""` added after email to make it a "controlled" value
        onChange={(event) => setEmail(event.target.value)}
        inputProps={{
          "data-testid": "emailInput",
        }}
        style={{ margin: "16px 0px" }}
      />
      {loginInitiated && !firstTimeLogin && (
        <FormControl fullWidth className={classes.select}>
          <InputLabel id="endpointSelectLabel">
            <FormattedMessage id="login.endpoint" />
          </InputLabel>
          <Select
            labelId="endpointSelectLabel"
            value={endpoint?.apiUrl || ""}
            label={intl.formatMessage({ id: "login.endpoint" })}
            onChange={(e) => {
              let val = e.target.value;
              if (val === "null") setAddEndpointDialogOpen(true);
              else setAddEndpointDialogOpen(false);
              setEndpoint(userEndpoints.find((e) => e.apiUrl === val));
            }}
          >
            {userEndpoints.map((e, i) => {
              return (
                <MenuItem key={e.id || i} value={e.apiUrl} sx={{ cursor: "pointer" }}>
                  <ListItemText
                    primary={
                      useMultipleEndpoints ? e.apiUrl : e.product ? productsPretty(e.product) : ""
                    }
                    secondary={`${e.product ? productsPretty(e.product) : ""}${
                      e.apiVersion ? ` - ${e.apiVersion}` : ""
                    }`}
                  />
                </MenuItem>
              );
            })}
            <MenuItem key="addNew" value="null" sx={{ cursor: "pointer" }}>
              <ListItemIcon>
                <AddIcon color="secondary" sx={{ fontSize: "1rem" }} />
              </ListItemIcon>
              <ListItemText primary={intl.formatMessage({ id: "menu.endpoints.addNew" })} />
            </MenuItem>
          </Select>
        </FormControl>
      )}
      {addEndpointDialogOpen && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
          }}
        >
          <StyledTextField
            select
            sx={{
              pt: 2,
              ...(useMultipleEndpoints ? { width: 1 / 4 } : { width: 1 / 1 }),
            }}
            value={newEndpointSystem}
            onChange={({ target: { value } }) => {
              setNewEndpointSystem(value);
              if (value === Product.VantageOnline) setNewEndpointUrl(voApiUrl);
              else if (value === Product.AsolviProtect) setNewEndpointUrl(asolviProtectApiUrl);
              else if (newEndpointUrl === voApiUrl) setNewEndpointUrl("");
            }}
          >
            {products.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.key}
              </MenuItem>
            ))}
          </StyledTextField>
          {useMultipleEndpoints ? (
            <StyledTextField
              sx={{ width: 3 / 4, ml: 1 }}
              label={intl.formatMessage({ id: "general.add" })}
              value={newEndpointUrl}
              onChange={({ target: { value } }) => setNewEndpointUrl(value)}
            />
          ) : null}
        </Box>
      )}
      {authVar.message && (
        <Alert severity="error" data-testid="loginResult">
          <AlertTitle>
            <FormattedMessage id="login.error" />
          </AlertTitle>
          {loginErrorMessage(authVar.message)}
        </Alert>
      )}
      {Array.isArray(userEndpointsError) &&
        userEndpointsError.length &&
        userEndpointsError[0].message === "Failed to fetch" && ( // TODO; JeezusFuckingChrist
          <Alert severity="error" data-testid="loginResult">
            <AlertTitle>
              <FormattedMessage id="login.error" />
            </AlertTitle>
            <FormattedMessage id="login.failedToFetchEndpoints" />
            <br />
            <FormattedMessage id="login.tryAgainLater" />
          </Alert>
        )}
      <div>
        <PrimaryButton
          key={`${preLoginLoading}`}
          type="submit"
          disabled={preLoginLoading}
          className={classes.submit}
          data-testid="loginButton"
        >
          <FormattedMessage id="login.signIn" />
        </PrimaryButton>
        {preLoginLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
      </div>
    </form>
  );
};
